import PropTypes from "prop-types";
import { useState } from "react";
import { FormControl, InputLabel, Select, MenuItem, Stack } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronUp, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "hooks/useTranslation";

function pickId(row, ...keys) {
  for (const key of keys) {
    if (row?.[key] != null && row[key] !== "") return row[key];
  }
  return "";
}

function pickName(row, ...keys) {
  for (const key of keys) {
    if (row?.[key] != null && row[key] !== "") return String(row[key]);
  }
  return "";
}

const selectSx = {
  height: 50,
  fontSize: "1rem",
  "& svg": {
    top: "calc(50% - 0.5em)",
    right: 10,
    position: "absolute",
    pointerEvents: "none",
    color: "gray",
  },
};

function HierarchySelect({
  id,
  label,
  value,
  onChange,
  options,
  idKey,
  nameKey,
  disabled,
  error,
  openDropdown,
  onOpen,
  onClose,
}) {
  return (
    <FormControl fullWidth error={error} disabled={disabled}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        value={value}
        label={label}
        onChange={(e) => onChange(e.target.value)}
        sx={selectSx}
        onOpen={onOpen}
        onClose={onClose}
        IconComponent={() => <FontAwesomeIcon icon={openDropdown ? faChevronUp : faChevronDown} />}
      >
        {options.map((row) => {
          const optionId = pickId(row, ...idKey);
          const optionName = pickName(row, ...nameKey);
          return (
            <MenuItem key={optionId} value={optionId}>
              {optionName}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

HierarchySelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  idKey: PropTypes.arrayOf(PropTypes.string).isRequired,
  nameKey: PropTypes.arrayOf(PropTypes.string).isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  openDropdown: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
};

export default function HierarchySelects({
  regions,
  departments,
  ias,
  iefs,
  selectedRegionId,
  selectedDepartmentId,
  selectedIaId,
  selectedIefId,
  onRegionChange,
  onDepartmentChange,
  onIaChange,
  onIefChange,
  disabled,
  districtError,
}) {
  const { t } = useTranslation();
  const [openDropdown, setOpenDropdown] = useState({});

  const setOpen = (field, isOpen) => setOpenDropdown((prev) => ({ ...prev, [field]: isOpen }));

  return (
    <Stack spacing={2}>
      <HierarchySelect
        id="region"
        label={t("hierarchy.region", "Region")}
        value={selectedRegionId}
        onChange={onRegionChange}
        options={regions}
        idKey={["regionId", "RegionId"]}
        nameKey={["regionName", "RegionName"]}
        disabled={disabled}
        openDropdown={openDropdown.region}
        onOpen={() => setOpen("region", true)}
        onClose={() => setOpen("region", false)}
      />
      <HierarchySelect
        id="department"
        label={t("hierarchy.department", "Department")}
        value={selectedDepartmentId}
        onChange={onDepartmentChange}
        options={departments}
        idKey={["departmentId", "DepartmentId"]}
        nameKey={["departmentName", "DepartmentName"]}
        disabled={disabled || !selectedRegionId}
        openDropdown={openDropdown.department}
        onOpen={() => setOpen("department", true)}
        onClose={() => setOpen("department", false)}
      />
      <HierarchySelect
        id="ia"
        label={t("hierarchy.ia", "Inspection Académie (IA)")}
        value={selectedIaId}
        onChange={onIaChange}
        options={ias}
        idKey={["iaId", "IaId"]}
        nameKey={["iaName", "IaName"]}
        disabled={disabled || !selectedRegionId}
        openDropdown={openDropdown.ia}
        onOpen={() => setOpen("ia", true)}
        onClose={() => setOpen("ia", false)}
      />
      <HierarchySelect
        id="ief"
        label={t("hierarchy.ief", "IEF")}
        value={selectedIefId}
        onChange={onIefChange}
        options={iefs}
        idKey={["iefId", "IefId"]}
        nameKey={["iefName", "IefName"]}
        disabled={disabled || !selectedDepartmentId || !selectedIaId}
        error={Boolean(districtError)}
        openDropdown={openDropdown.ief}
        onOpen={() => setOpen("ief", true)}
        onClose={() => setOpen("ief", false)}
      />
      {districtError && (
        <p style={{ color: "red", fontSize: "0.75rem", marginTop: -8 }}>{districtError}</p>
      )}
    </Stack>
  );
}

HierarchySelects.propTypes = {
  regions: PropTypes.arrayOf(PropTypes.object).isRequired,
  departments: PropTypes.arrayOf(PropTypes.object).isRequired,
  ias: PropTypes.arrayOf(PropTypes.object).isRequired,
  iefs: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedRegionId: PropTypes.string.isRequired,
  selectedDepartmentId: PropTypes.string.isRequired,
  selectedIaId: PropTypes.string.isRequired,
  selectedIefId: PropTypes.string.isRequired,
  onRegionChange: PropTypes.func.isRequired,
  onDepartmentChange: PropTypes.func.isRequired,
  onIaChange: PropTypes.func.isRequired,
  onIefChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  districtError: PropTypes.string,
};

HierarchySelects.defaultProps = {
  disabled: false,
  districtError: "",
};
